import { useState } from 'react';
import { useQuery } from 'react-query';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Chip
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { adminAPI } from '../services/api';
import useListQueryParams from '../hooks/useListQueryParams';
import AdminDateRangeFilter from '../components/AdminDateRangeFilter.jsx';
import './ModuleAdmin.css';

function formatWhen(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });
}

function formatPhoneNumber(phone) {
  if (!phone) return '—';
  const raw = String(phone).replace(/\D/g, '');
  if (raw.length === 10) {
    return `+91 ${raw.slice(0, 5)} ${raw.slice(5)}`;
  }
  if (raw.length === 12 && raw.startsWith('91')) {
    return `+91 ${raw.slice(2, 7)} ${raw.slice(7)}`;
  }
  return phone.startsWith('+') ? phone : `+${phone}`;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Sent', label: 'Sent' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Read', label: 'Read' },
  { value: 'Failed', label: 'Failed' }
];

const TEMPLATE_OPTIONS = [
  { value: '', label: 'All templates' },
  { value: 'registration_initiated', label: 'Registration Initiated' },
  { value: 'payment_success_booker', label: 'Payment Success (Booker)' },
  { value: 'payment_success_participant', label: 'Payment Success (Participant)' },
  { value: 'cancellation_request_received', label: 'Cancellation Request' }
];

function statusColor(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'delivered') return 'success';
  if (s === 'read') return 'primary';
  if (s === 'sent') return 'info';
  if (s === 'failed') return 'error';
  return 'default';
}

export default function WaplifyLogs() {
  const { query, setQuery } = useListQueryParams({
    page: '1',
    limit: '50',
    search: '',
    status: '',
    template: '',
    fromDate: '',
    toDate: '',
    sortOrder: 'desc'
  });

  const [selectedEntry, setSelectedEntry] = useState(null);

  const page = Number(query.page || 1);
  const limit = Number(query.limit || 50);
  const search = query.search || '';
  const statusFilter = query.status || '';
  const templateFilter = query.template || '';
  const fromDate = query.fromDate || '';
  const toDate = query.toDate || '';
  const sortOrder = query.sortOrder || 'desc';

  const params = { page, limit, sortOrder };
  if (search.trim()) params.search = search.trim();
  if (statusFilter) params.status = statusFilter;
  if (templateFilter) params.template = templateFilter;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-waplify-logs', page, limit, search, statusFilter, templateFilter, fromDate, toDate, sortOrder],
    queryFn: () => adminAPI.getWaplifyWebhookLogs(params)
  });

  const list = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: 0 };

  return (
    <div className="module-page">
      <PageHeader
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <WhatsAppIcon sx={{ color: '#25D366' }} />
            WhatsApp / Waplify Logs
          </Box>
        }
        subtitle="Track all automated WhatsApp transactional messages, customer notifications, delivery statuses, and provider API responses in real-time."
      />

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="Name, phone, message ID, content…"
          value={search}
          onChange={(e) => setQuery({ search: e.target.value, page: '1' })}
          sx={{ minWidth: 260 }}
        />
        <TextField
          size="small"
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setQuery({ status: e.target.value, page: '1' })}
          sx={{ minWidth: 140 }}
        >
          {STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value || 'all'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label="Template"
          value={templateFilter}
          onChange={(e) => setQuery({ template: e.target.value, page: '1' })}
          sx={{ minWidth: 200 }}
        >
          {TEMPLATE_OPTIONS.map((o) => (
            <MenuItem key={o.value || 'all'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <AdminDateRangeFilter
          startDate={fromDate}
          endDate={toDate}
          onStartDateChange={(v) => setQuery({ fromDate: v, page: '1' })}
          onEndDateChange={(v) => setQuery({ toDate: v, page: '1' })}
        />
        <TextField
          size="small"
          select
          label="Sort"
          value={sortOrder}
          onChange={(e) => setQuery({ sortOrder: e.target.value, page: '1' })}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="desc">Latest first</MenuItem>
          <MenuItem value="asc">Oldest first</MenuItem>
        </TextField>
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading WhatsApp logs…</p>
        ) : list.length === 0 ? (
          <EmptyState
            title="No WhatsApp logs found"
            description="Sent WhatsApp messages and delivery webhooks will appear here."
          />
        ) : (
          <table className="module-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Participant</th>
                <th>Trek Name</th>
                <th>WhatsApp Number</th>
                <th>Template</th>
                <th>Message Preview</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatWhen(row.createdAt)}</td>
                  <td>
                    <strong>{row.participantName || '—'}</strong>
                  </td>
                  <td>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{row.trekName || '—'}</span>
                  </td>
                  <td>
                    <span className="users-phone">{formatPhoneNumber(row.contactPhone)}</span>
                  </td>
                  <td>
                    <code style={{ fontSize: '0.82rem', background: '#f4f4f5', padding: '2px 6px', borderRadius: 4 }}>
                      {row.templateName || '—'}
                    </code>
                  </td>
                  <td style={{ maxWidth: 300 }}>
                    <div style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.85rem'
                    }} title={row.message}>
                      {row.message ? row.message.slice(0, 100) : '—'}
                    </div>
                  </td>
                  <td>
                    <Chip
                      size="small"
                      label={row.status || 'Sent'}
                      color={statusColor(row.status)}
                      variant={row.status === 'Failed' ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  </td>
                  <td>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<VisibilityRoundedIcon fontSize="small" />}
                      onClick={() => setSelectedEntry(row)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {pagination.totalPages > 1 && (
          <div className="module-list-pagination">
            <span>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <div className="module-list-pagination-btns">
              <button
                type="button"
                className="module-btn secondary"
                disabled={page <= 1}
                onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}
              >
                Previous
              </button>
              <button
                type="button"
                className="module-btn secondary"
                disabled={page >= pagination.totalPages}
                onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Log Details Modal */}
      <Dialog
        open={Boolean(selectedEntry)}
        onClose={() => setSelectedEntry(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsAppIcon sx={{ color: '#25D366' }} />
            <Typography variant="h6" component="div" fontWeight={600}>
              WhatsApp Message Details
            </Typography>
          </Box>
          <IconButton onClick={() => setSelectedEntry(null)} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {selectedEntry && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.5 }}>
                <div>
                  <Typography variant="caption" color="text.secondary">Time (IST)</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatWhen(selectedEntry.createdAt)}</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Participant</Typography>
                  <Typography variant="body2" fontWeight={600}>{selectedEntry.participantName || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Trek Name</Typography>
                  <Typography variant="body2" fontWeight={600}>{selectedEntry.trekName || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">WhatsApp Number</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatPhoneNumber(selectedEntry.contactPhone)}</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <div>
                    <Chip
                      size="small"
                      label={selectedEntry.status}
                      color={statusColor(selectedEntry.status)}
                      variant={selectedEntry.status === 'Failed' ? 'filled' : 'outlined'}
                    />
                  </div>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Template</Typography>
                  <Typography variant="body2" fontWeight={600}>{selectedEntry.templateName || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary">Message ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {selectedEntry.providerMessageId || '—'}
                  </Typography>
                </div>
              </Box>

              {selectedEntry.errorMessage && (
                <Box sx={{ p: 1.5, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1.5 }}>
                  <Typography variant="caption" color="error.main" fontWeight={700}>
                    Error ({selectedEntry.errorCode || 'WAPLIFY_ERROR'}):
                  </Typography>
                  <Typography variant="body2" color="error.dark">
                    {selectedEntry.errorMessage}
                  </Typography>
                </Box>
              )}

              <div>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Rendered Message Content
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: 2,
                    fontFamily: 'inherit',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.88rem',
                    lineHeight: 1.5,
                    color: '#14532d'
                  }}
                >
                  {selectedEntry.message || '—'}
                </Box>
              </div>

              <div>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Raw API Payload & Webhook Response
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    p: 2,
                    bgcolor: '#1e293b',
                    color: '#f8fafc',
                    borderRadius: 2,
                    fontSize: '0.78rem',
                    overflowX: 'auto',
                    maxHeight: 280
                  }}
                >
                  {JSON.stringify(selectedEntry.payload || {}, null, 2)}
                </Box>
              </div>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSelectedEntry(null)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
